/**
 * The settlement of a billing account and its refunds: the responsibility,
 * allocation and refund registries, the allocate/reverse/reallocate forms
 * with their cross-owner statement, and the refund preview and record form.
 * The rest of the finance staff workspace is `finance.ts`.
 */
export const financeSettlement = {
  "finance.settlement.responsibilitiesTitle": "Responsibility",
  "finance.settlement.responsibilitiesTableLabel": "Responsibility positions",
  "finance.settlement.responsibilitiesLoading": "Loading responsibility positions",
  "finance.settlement.responsibilitiesFailed":
    "The responsibility positions could not be loaded.",
  "finance.settlement.responsibilitiesEmpty": "No responsibility positions",
  "finance.settlement.responsibilitiesEmptyDetail":
    "Approved obligations money can be held against will stand here.",
  "finance.settlement.columnServiceDate": "Service date",
  "finance.settlement.columnBeneficiary": "Beneficiary",
  "finance.settlement.columnDueDate": "Due",
  "finance.settlement.columnOutstanding": "Outstanding",
  "finance.settlement.columnAllocatable": "Allocatable",
  "finance.settlement.columnSettled": "Settled",
  "finance.settlement.dueStatus.unresolved": "Unscheduled",
  "finance.settlement.dueStatus.not_due": "Not due",
  "finance.settlement.dueStatus.due": "Due today",
  "finance.settlement.dueStatus.overdue": "Overdue",
  "finance.settlement.dueStatus.settled": "Settled",
  "finance.settlement.allocationsTitle": "Allocations",
  "finance.settlement.allocationsTableLabel": "Allocations",
  "finance.settlement.allocationsLoading": "Loading allocations",
  "finance.settlement.allocationsFailed": "The allocations could not be loaded.",
  "finance.settlement.allocationsEmpty": "No allocations yet",
  "finance.settlement.allocationsEmptyDetail":
    "Funds bound to responsibility positions will stand here.",
  "finance.settlement.columnPostingDate": "Posting date",
  "finance.settlement.columnKind": "Kind",
  "finance.settlement.columnAmount": "Amount",
  "finance.settlement.columnReversible": "Reversible",
  "finance.settlement.columnRecorded": "Recorded",
  "finance.settlement.kindAllocation": "Allocation",
  "finance.settlement.kindReversal": "Reversal",
  "finance.settlement.digestTitle": "Allocation",
  "finance.settlement.allocate": "Allocate funds",
  "finance.settlement.reverse": "Reverse",
  "finance.settlement.reallocate": "Reallocate",
  "finance.settlement.allocateTitle": "Allocate funds",
  "finance.settlement.allocateDetail":
    "Bind a receipt's available money to one approved outstanding position.",
  "finance.settlement.allocateSubmit": "Allocate",
  "finance.settlement.allocateSubmitting": "Allocating",
  "finance.settlement.allocateUnsaved": "an unsent allocation",
  "finance.settlement.reverseTitle": "Release funds",
  "finance.settlement.reverseDetail":
    "Return part of this allocation to its original receipt.",
  "finance.settlement.reverseSubmit": "Release",
  "finance.settlement.reverseSubmitting": "Releasing",
  "finance.settlement.reverseUnsaved": "an unsent release",
  "finance.settlement.reallocateTitle": "Reallocate funds",
  "finance.settlement.reallocateDetail":
    "Release part of this allocation and bind it to another position, as one command.",
  "finance.settlement.reallocateSubmit": "Reallocate",
  "finance.settlement.reallocateSubmitting": "Reallocating",
  "finance.settlement.reallocateUnsaved": "an unsent reallocation",
  "finance.settlement.unsavedDetail": "The typed details will be discarded.",
  "finance.settlement.receiptField": "Source receipt",
  "finance.settlement.receiptRequired": "Choose the receipt the money comes from.",
  "finance.settlement.receiptOption": "{date} — available {amount}",
  "finance.settlement.targetOwnerField": "Target owner",
  "finance.settlement.targetOwnerPlaceholder": "Search people and legal entities",
  "finance.settlement.targetAccountNone": "This owner has no active billing account.",
  "finance.settlement.targetFailed": "The target account could not be read.",
  "finance.settlement.portionField": "Responsibility position",
  "finance.settlement.portionRequired": "Choose the position the money is held against.",
  "finance.settlement.portionOption": "{date} — allocatable {amount}",
  "finance.settlement.amountField": "Amount",
  "finance.settlement.amountRequired": "Type the amount to move.",
  "finance.settlement.amountExceeds":
    "The amount is larger than what this source or position allows.",
  "finance.settlement.instructionField": "Instruction reference",
  "finance.settlement.instructionRequired":
    "Name the instruction this move follows — a document, message or slip.",
  "finance.settlement.crossOwnerConfirm":
    "The account owner instructed this move to another party's account.",
  "finance.settlement.crossOwnerRequired": "Confirm the owner's instruction.",
  "finance.settlement.reasonField": "Reason",
  "finance.settlement.reasonRequired": "State the reason.",
  "finance.settlement.accountsLoading": "Reading the accounts",
  "finance.settlement.accountsFailed": "The accounts could not be read.",
  "finance.settlement.reload": "Read the accounts again",
  "finance.settlement.reloadFailed": "The accounts could not be read again.",
  "finance.settlement.reloaded":
    "The accounts were read again. Check the draft, then send it.",
  "finance.settlement.failed": "The settlement command could not be completed.",
  "finance.settlement.conflict":
    "An account changed while you were working. Read the accounts again, then send.",
  "finance.settlement.accountStateConflict":
    "The account's lifecycle does not allow this move.",
  "finance.settlement.eligibilityConflict":
    "The position's eligibility changed. Read the accounts again and check the position.",
  "finance.settlement.responsibilityExceeded":
    "The amount exceeds what this position still allows.",
  "finance.settlement.fundsInsufficient":
    "The receipt no longer holds that much available money.",
  "finance.settlement.fundsRestricted":
    "This receipt's money is restricted to one patient's benefit and cannot fund this position.",
  "finance.settlement.reversalExceeded":
    "The amount exceeds what this allocation can still release.",
  "finance.settlement.delegationUnavailable":
    "The delegation this move relies on is not available.",
  "finance.settlement.periodClosed":
    "The financial period of that date is closed. Reopen it under Finance administration first.",
  "finance.settlement.amountInvalid":
    "The amount could not be read. Type it in the clinic's number format.",
  "finance.settlement.amountOverflow": "The amount is too large.",
  "finance.settlement.currencyMismatch":
    "The amount's currency does not match the account's.",
  "finance.settlement.attemptConflict":
    "This attempt was already sent with different details. Close the form and start again.",
  "finance.settlement.forbidden": "You do not have permission to move held funds.",
  "finance.settlement.invalid": "Check the entered details.",

  "finance.refunds.title": "Refunds",
  "finance.refunds.tableLabel": "Refunds",
  "finance.refunds.loading": "Loading refunds",
  "finance.refunds.loadFailed": "The refunds could not be loaded.",
  "finance.refunds.empty": "No refunds recorded",
  "finance.refunds.emptyDetail": "Actual payouts from this account will stand here.",
  "finance.refunds.columnCashDate": "Cash date",
  "finance.refunds.columnRecipient": "Recipient",
  "finance.refunds.columnMethod": "Method",
  "finance.refunds.columnAmount": "Amount",
  "finance.refunds.columnRecorded": "Recorded",
  "finance.refunds.recordAction": "Record refund",
  "finance.refunds.recordTitle": "Record a refund",
  "finance.refunds.recordDetail":
    "The evidence of an actual payout back to this receipt's original payer.",
  "finance.refunds.recordSubmit": "Record refund",
  "finance.refunds.recordSubmitting": "Recording",
  "finance.refunds.previewLoading": "Reading the refund cap",
  "finance.refunds.previewFailed": "The refund cap could not be read.",
  "finance.refunds.originalPayer": "Original payer",
  "finance.refunds.originalMethod": "Original method",
  "finance.refunds.originalCashDate": "Original cash date",
  "finance.refunds.maximum": "Available to refund",
  "finance.refunds.amountField": "Amount",
  "finance.refunds.amountRequired": "Type the amount paid out.",
  "finance.refunds.amountExceedsMax":
    "The amount is larger than this receipt can refund.",
  "finance.refunds.methodField": "Method",
  "finance.refunds.methodChangeReasonField": "Method change reason",
  "finance.refunds.methodChangeReasonRequired":
    "State why the payout method differs from the original.",
  "finance.refunds.cashDateField": "Cash date",
  "finance.refunds.evidenceField": "Evidence reference",
  "finance.refunds.evidenceRequired":
    "Name the payout's evidence — a slip, transfer or register reference.",
  "finance.refunds.reasonField": "Reason",
  "finance.refunds.reasonRequired": "State the reason.",
  "finance.refunds.unsavedIntent": "an unrecorded refund",
  "finance.refunds.unsavedDetail": "The typed refund will be discarded.",
  "finance.refunds.reload": "Read the receipt again",
  "finance.refunds.reloadFailed": "The receipt could not be read again.",
  "finance.refunds.reloaded":
    "The receipt was read again. Check the draft, then send it.",
  "finance.refunds.failed": "The refund could not be recorded.",
  "finance.refunds.conflict":
    "The account changed while you were working. Read the receipt again, then send.",
  "finance.refunds.exceedsAvailable":
    "The receipt no longer holds that much to refund. Read the receipt again.",
  "finance.refunds.evidenceAlreadyRecorded":
    "A refund with this evidence was already recorded on this receipt.",
  "finance.refunds.periodClosed":
    "The financial period of that cash date is closed. Reopen it under Finance administration or choose another date.",
  "finance.refunds.amountInvalid":
    "The amount could not be read. Type it in the clinic's number format.",
  "finance.refunds.amountOverflow": "The amount is too large.",
  "finance.refunds.currencyMismatch":
    "The amount's currency does not match the account's.",
  "finance.refunds.attemptConflict":
    "This attempt was already sent with different details. Close the form and start again.",
  "finance.refunds.forbidden": "You do not have permission to record refunds.",
  "finance.refunds.invalid": "Check the entered refund details.",
} as const;
